import { FC, useContext, useEffect, useState } from 'react';
import { FilterType } from 'src/enums/common';
import PropTypes from 'prop-types';
import { FilterFormat, FilterProperties } from 'src/types/filter';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@material-ui/core';
import { FilterListOutlined } from '@material-ui/icons';
import { containsOperator,
  endsWithOperator,
  equalOperator,
  greaterThanOperator,
  inOperator,
  isAfterOperator,
  isBeforeOperator,
  isBlankOperator,
  isPresentOperator,
  lessThanOperator,
  notContainsOperator,
  notEqualOperator,
  startsWithOperator } from 'src/utils/filter-operators';
import { getStartOfDay } from 'src/utils/date-format';
import CloseIcon from '@material-ui/icons/Close';
import viewContext from 'src/viewContext';
import { Roles } from 'src/enums/role-based-auth-guard';
import { useAuth } from 'src/hooks/use-auth';
import { DropDown } from './dropdown';
import { MultiSelectDropDown } from './multiselect-dropdown';
import { BasicTextField } from './basic-text-field';
import { BasicDatePicker } from './basic-date-picker';

interface FilterBarProps {
  filters: Array<FilterProperties>;
  onApply: (newFilters: Array<FilterFormat>) => void;
  onApplyExport?: () => void;
}

interface FilterState{
  dbProperty: string,
  type: FilterType,
  idList?: Set<string>,
  valuesList?: Set<string>,
  id?: string,
  value?: string,
  operator?: {
    label: string,
    typesAccepted: Array<string>,
    value: string
  }
}

const filterOperators = {
  Equals: equalOperator,
  NotEquals: notEqualOperator,
  Contains: containsOperator,
  NotContains: notContainsOperator,
  StartsWith: startsWithOperator,
  EndsWith: endsWithOperator,
  GreaterThan: greaterThanOperator,
  LessThan: lessThanOperator,
  IsAfter: isAfterOperator,
  IsBefore: isBeforeOperator,
  IsBlank: isBlankOperator,
  IsPresent: isPresentOperator,
  In: inOperator
};
export const FilterBar: FC<FilterBarProps> = (props) => {
  const { filters, onApply, onApplyExport } = props;
  const { mobileView } = useContext(viewContext);
  const [filtersMap, setFilterMap] = useState<Map<string, FilterState>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isVisible, setIsVisible] = useState<boolean>(!mobileView);
  // States for from and to date validation
  const [fromDate, setFormDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();
  const updateFiltersMap = (key:string, newState: FilterState) => {
    setFilterMap(filtersMap.set(key, newState));
  };
  const auth = useAuth();
  // Setup filtersMap state
  useEffect(() => {
    filters.forEach((filter) => {
      // If condition will retain the state of previous selection for given filter
      if (!filtersMap.has(filter.label)) {
        const newState: FilterState = {
          dbProperty: filter.dbProperty,
          type: filter.filterType,
          idList: new Set(),
          valuesList: new Set(),
          operator: filter.operator || null
        };
        updateFiltersMap(filter.label, newState);
      }
    });
    setIsLoading(false);
  }, [filters]);
  // Handles multi select dropdown selection list
  const multiSelectionHandler = (event) => {
    // Selecte id from dropdown
    const selectedId = event.target.value[event.target.value.length - 1]
      ? event.target.value[event.target.value.length - 1].trim()
      : null;
    const index = parseInt(event.target.name, 10);
    const filter = filters[index];
    // Selected value corresponding to selected id
    const selectedValue = selectedId
      ? (filter.options.filter((item) => (
        item.id ? item.id.toString() === selectedId.toString() : false
      )))
      : null;
    // Updating filterMap state
    setFilterMap((prevState) => {
      const oldState = prevState.get(filter.label);
      const idSet = new Set(oldState.idList);
      const valueSet = new Set(oldState.valuesList);
      if (oldState.idList.has(selectedId)) {
        // Remove Id
        idSet.delete(selectedId);
        // Remove Value
        const value = selectedValue ? selectedValue[0].value : null;
        valueSet.delete(value);
      } else {
        // Add id
        idSet.add(selectedId);
        // Add value
        const value = selectedValue ? selectedValue[0].value : null;
        valueSet.add(value);
      }
      const newState: FilterState = {
        ...oldState,
        idList: idSet,
        valuesList: valueSet
      };
      const newMap = new Map(prevState);
      newMap.set(filter.label, newState);
      return newMap;
    });
  };
  // Handles single dropdown selection
  const singleSelectionHandler = (event) => {
    const selectedId = event.target.value;
    const index = parseInt(event.target.name, 10);
    const filter = filters[index];
    const selectedValue = filter.options.filter((item) => item.id === selectedId);
    setFilterMap((prevState) => {
      const oldState = prevState.get(filter.label);
      const newState: FilterState = {
        ...oldState,
        id: selectedId,
        value: selectedValue.length > 0 ? selectedValue[0].value : ''
      };
      const newMap = new Map(prevState);
      newMap.set(filter.label, newState);
      return newMap;
    });
  };
  // Handles text input
  const textInputHandler = (event) => {
    const typedValue = event.target.value;
    const index = parseInt(event.target.name, 10);
    const filter = filters[index];
    setFilterMap((prevState) => {
      const oldState = prevState.get(filter.label);
      const newState: FilterState = {
        ...oldState,
        value: typedValue
      };
      const newMap = new Map(prevState);
      newMap.set(filter.label, newState);
      return newMap;
    });
  };
  // Handles date input
  const dateInputHandler = (event) => {
    const { index, selectedValue, label } = event;
    const newDate = getStartOfDay(new Date(selectedValue)).toISOString();
    const filter = filters[index];
    if (label.toString().toLowerCase().indexOf('till') >= 0) {
      setToDate(getStartOfDay(new Date(newDate)));
    } else if (label.toString().toLowerCase().indexOf('from') >= 0) {
      setFormDate(getStartOfDay(new Date(newDate)));
    }
    setFilterMap((prevState) => {
      const oldState = prevState.get(filter.label);
      const newState: FilterState = {
        ...oldState,
        value: newDate
      };
      const newMap = new Map(prevState);
      newMap.set(filter.label, newState);
      return newMap;
    });
  };

  const applyFilterHandler = () => {
    // Iterate over filtersMap
    const filtersList: FilterFormat[] = [];
    const getFilterObject = (): FilterFormat => ({
      property: '',
      value: '',
      operator: ''
    });
    filtersMap.forEach((filterDetails) => {
      switch (filterDetails.type) {
        case FilterType.TEXT: {
          if (filterDetails.value && filterDetails.value.trim()) {
            const filter: FilterFormat = getFilterObject();
            filter.property = filterDetails.dbProperty;
            filter.operator = filterDetails.operator
              ? filterDetails.operator.value
              : filterOperators.Contains.value;
            filter.value = filterDetails.value.trim();
            filtersList.push(filter);
          }
          break;
        }
        case FilterType.NUMBER: {
          if (filterDetails.value && filterDetails.value.trim()) {
            const filter: FilterFormat = getFilterObject();
            filter.property = filterDetails.dbProperty;
            filter.operator = filterDetails.operator
              ? filterDetails.operator.value
              : filterOperators.Equals.value;
            filter.value = parseFloat(filterDetails.value.trim());
            filtersList.push(filter);
          }
          break;
        }
        case FilterType.SINGLE_SELECT: {
          if (filterDetails.value) {
            const filter: FilterFormat = getFilterObject();
            filter.property = filterDetails.dbProperty;
            filter.operator = filterDetails.operator
              ? filterDetails.operator.value
              : filterOperators.Equals.value;
            if (typeof filterDetails.value === 'string') {
              if (filterDetails.value.trim()) {
                filter.value = filterDetails.value.trim();
                filtersList.push(filter);
              }
            } else {
              filter.value = filterDetails.value;
              filtersList.push(filter);
            }
          }
          break;
        }
        case FilterType.DATE: {
          if (filterDetails.value && filterDetails.value.trim()) {
            const filter: FilterFormat = getFilterObject();
            filter.property = filterDetails.dbProperty;
            filter.operator = filterDetails.operator
              ? filterDetails.operator.value
              : filterOperators.Equals.value;
            filter.value = filterDetails.value.trim();
            filtersList.push(filter);
          }
          break;
        }
        case FilterType.MULTI_SELECT: {
          if (filterDetails.idList.size > 0) {
            const filter: FilterFormat = getFilterObject();
            filter.property = filterDetails.dbProperty;
            filter.operator = filterOperators.In.value;
            filter.value = Array.from(filterDetails.idList);
            filtersList.push(filter);
          }
          break;
        }
        default: {
          break;
        }
      }
    });
    onApply(filtersList);
  };
  const clearFiltersHandler = () => {
    const filtersList: FilterFormat[] = [];
    setFilterMap((prevState) => {
      const tempMap = new Map(prevState);
      tempMap.forEach((filterState) => {
        const tempFilterState = filterState;
        if ('idList' in tempFilterState) {
          tempFilterState.idList = new Set();
        }
        if ('valuesList' in tempFilterState) {
          tempFilterState.valuesList = new Set();
        }
        if ('id' in tempFilterState) {
          tempFilterState.id = undefined;
        }
        if ('value' in tempFilterState) {
          tempFilterState.value = undefined;
        }
      });
      onApply(filtersList);
      return tempMap;
    });
  };

  const getActionsView = () => (
    <>

      <Button
        color="primary"
        size="small"
        sx={{ ml: 1, float: 'right', height: '34.59px' }}
        onClick={() => {
          clearFiltersHandler();
          if (mobileView) {
            setIsVisible(false);
          }
        }}
        variant="outlined"
      >
        Clear
      </Button>

      { (onApplyExport && (auth.user.scope === Roles.WEB_ADMIN || auth.user.scope === Roles.DEALER))
  && (
    <Button
      color="primary"
      size="small"
      sx={{ ml: 1, float: 'right', height: '34.59px' }}
      onClick={() => {
        onApplyExport();
        if (mobileView) {
          setIsVisible(false);
        }
      }}
      variant="outlined"
    >
      Export
    </Button>
  )}

      <Button
        color="primary"
        size="small"
        type="submit"
        sx={{ ml: 1, float: 'right', height: '34.59px' }}
        onClick={() => {
          applyFilterHandler();
          if (mobileView) {
            setIsVisible(false);
          }
        }}
        variant="contained"
      >
        Apply
      </Button>

    </>
  );

  const getFilterView = () => {
    const view = (
      <Grid
        container
        mt={2}
        mb={1}
        pr={mobileView ? 0 : 2}
      >
        {filters.map((filterEntry, index) => {
          const filterState = filtersMap.get(filterEntry.label);
          if (filterEntry.filterType === FilterType.SINGLE_SELECT) {
            return (
              <Grid
                key={filterEntry.label}
                item
                // width={mobileView ? '90%' : '160px'}
                lg={2}
                md={3}
                sm={mobileView ? 12 : 4}
                xs={12}
                pr={mobileView ? 2 : 1}
              >
                <DropDown
                  label={filterEntry.label}
                  index={index.toString()}
                  options={filterEntry.options}
                  onSelect={singleSelectionHandler}
                  selectedOption={filterState.value}
                />
              </Grid>
            );
          }
          if (filterEntry.filterType === FilterType.MULTI_SELECT) {
            return (
              <Grid
                key={filterEntry.label}
                item
                // width={mobileView ? '90%' : '160px'}
                lg={2}
                md={3}
                sm={mobileView ? 12 : 4}
                pr={mobileView ? 2 : 1}
                xs={12}
              >
                <MultiSelectDropDown
                  label={filterEntry.label}
                  index={index.toString()}
                  options={filterEntry.options}
                  onSelect={multiSelectionHandler}
                  selectedOptions={[...filterState.valuesList]}
                  mlRequierd
                />
              </Grid>
            );
          }
          if (filterEntry.filterType === FilterType.DATE) {
            return (
              <Grid
                key={filterEntry.label}
                item
                // width={mobileView ? '90%' : '160px'}
                lg={2}
                md={3}
                sm={mobileView ? 12 : 4}
                pr={mobileView ? 2 : 1}
                xs={12}
              >
                <BasicDatePicker
                  selectedDate={new Date(filterState.value)}
                  onSelect={dateInputHandler}
                  label={filterEntry.label}
                  index={index.toString()}
                  maxDate={(filterEntry.operator
                && (filterEntry.operator.value === isAfterOperator.value)
                && toDate) ? toDate : undefined}
                  minDate={(filterEntry.operator
                && (filterEntry.operator.value === isBeforeOperator.value)
                && fromDate) ? fromDate : undefined}
                />
              </Grid>
            );
          }
          if (filterEntry.filterType === FilterType.TEXT) {
            console.log('filterEntry', filterEntry);
            return (
              <Grid
                key={filterEntry.label}
                item
                // width={mobileView ? '90%' : '160px'}
                lg={2}
                md={3}
                pr={mobileView ? 2 : 1}
                sm={mobileView ? 12 : 4}
                xs={12}
              >
                <BasicTextField
                  label={filterEntry.label}
                  index={index.toString()}
                  value={filterState.value}
                  onChange={textInputHandler}
                />
              </Grid>
            );
          }
          if (filterEntry.filterType === FilterType.NUMBER) {
            return (
              <Grid
                key={filterEntry.label}
                item
                // width={mobileView ? '90%' : '160px'}
                lg={2}
                md={3}
                pr={mobileView ? 2 : 1}
                sm={mobileView ? 12 : 4}
                xs={12}
              >
                <BasicTextField
                  label={filterEntry.label}
                  index={index.toString()}
                  value={filterState.value}
                  onChange={textInputHandler}
                />
              </Grid>
            );
          }
          return null;
        })}
        {!mobileView && (
        <Box
          flexGrow={1}
        >
          {getActionsView()}
        </Box>
        )}
      </Grid>
    );

    if (!mobileView) {
      return view;
    }
    return (
      <Dialog
        scroll="paper"
        open={isVisible}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        <DialogTitle
          sx={{ p: 1, pl: 2 }}
        >
          Apply Filters
          <IconButton
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
            color="inherit"
            onClick={() => {
              setIsVisible(false);
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ px: 2 }}
          dividers
        >
          {view}
        </DialogContent>
        <DialogActions
          sx={{ p: 2 }}
        >
          {getActionsView()}
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div className="filter-bar">
      <form onSubmit={(e) => { e.preventDefault(); applyFilterHandler(); }}>
        <Grid
          container
          spacing={1}
        >
          <Grid
            item
            md={12}
            sm={12}
          >
            { !isLoading && (
            <Box sx={{
              display: 'flex',
              alignItems: 'top'
            }}
            >
              <FilterListOutlined
                fontSize="large"
                sx={{ my: 1.5, ml: 2, cursor: 'pointer' }}
                onClick={() => { setIsVisible(!isVisible); }}
              />
              {isVisible
              && (
                <>{getFilterView()}</>
              )}
            </Box>
            )}
          </Grid>

        </Grid>
      </form>
    </div>
  );
};
FilterBar.propTypes = {
  filters: PropTypes.array,
  onApply: PropTypes.func,
  onApplyExport: PropTypes.func,
};
